import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Save, Plus, Trash2, Gem, Coins, Swords } from 'lucide-react';
import { api } from '../../lib/api';
import { SkeletonList, SectionHead } from '../../components/ui';

const ECO_FIELDS = [
  ['points_per_unit', 'Points per 1 currency unit', 'How many points for 1 RON (base currency)'],
  ['signup_bonus', 'Signup bonus', 'Points given on registration'],
  ['referral_signup_referrer', 'Referral · inviter signup bonus', ''],
  ['referral_signup_referee', 'Referral · new user signup bonus', ''],
  ['referral_referrer_pct', 'Referral · % to inviter per purchase', '% of the points the buyer earns'],
  ['referral_referee_pct', 'Referral · % extra to buyer', ''],
  ['share_points', 'Points per share', ''],
  ['share_click_points', 'Points per 5 clicks on a share', ''],
  ['share_conversion_points', 'Points per share conversion', ''],
  ['max_share_per_day', 'Max rewarded shares / day', 'Anti-abuse cap'],
  ['rating_points', 'Points for rating a shop', ''],
  ['daily_base', 'Daily reward · base', ''],
  ['daily_streak_step', 'Daily reward · per streak day', ''],
  ['daily_max', 'Daily reward · max', ''],
  ['fight_win_points', 'Duel win points', ''],
  ['fight_loss_points', 'Duel loss points', ''],
  ['fight_win_xp', 'Duel win XP', ''],
  ['fight_loss_xp', 'Duel loss XP', ''],
];

export default function AdminEconomy() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['adminSettings'], queryFn: () => api.get('/admin/settings').then(r => r.data) });
  const [eco, setEco] = useState(null);
  const [tiers, setTiers] = useState(null);
  const [combat, setCombat] = useState(null);
  const [cur, setCur] = useState({ code: '', rate: '' });

  useEffect(() => {
    if (data) { setEco({ ...data.economy }); setTiers(JSON.parse(JSON.stringify(data.loyalty_tiers))); setCombat({ ...data.combat }); }
  }, [data]);

  const save = useMutation({
    mutationFn: ({ key, value }) => api.put(`/admin/settings/${key}`, { value }),
    onSuccess: () => { toast.success('Settings saved'); qc.invalidateQueries(['adminSettings']); },
    onError: e => toast.error(e.message),
  });
  const saveCur = useMutation({
    mutationFn: ({ code, rate }) => api.put(`/admin/currencies/${code}`, { rate_to_base: rate }),
    onSuccess: () => { toast.success('Currency saved'); setCur({ code: '', rate: '' }); qc.invalidateQueries(['adminSettings']); },
    onError: e => toast.error(e.message),
  });
  const delCur = useMutation({
    mutationFn: (code) => api.delete(`/admin/currencies/${code}`),
    onSuccess: () => { toast.success('Deleted'); qc.invalidateQueries(['adminSettings']); },
    onError: e => toast.error(e.message),
  });

  if (isLoading || !eco) return <SkeletonList n={5} h="h-24" />;

  return (
    <div className="space-y-4">
      {/* ECONOMY */}
      <div className="card p-4">
        <SectionHead title="Points economy" sub="Global gamification rules" right={
          <button onClick={() => save.mutate({ key: 'economy', value: eco })} className="btn-primary btn-sm">
            <Save size={13} /> Save</button>} />
        <div className="grid sm:grid-cols-2 gap-3">
          {ECO_FIELDS.map(([k, l, d]) => (
            <div key={k}>
              <label className="label">{l}</label>
              <input className="input !py-2" type="number" step="0.01" value={eco[k] ?? ''}
                onChange={e => setEco({ ...eco, [k]: Number(e.target.value) })} />
              {d && <p className="text-[10px] text-ink-400 font-semibold mt-0.5">{d}</p>}
            </div>))}
          <div>
            <label className="label">Base currency</label>
            <input className="input !py-2" value={eco.base_currency}
              onChange={e => setEco({ ...eco, base_currency: e.target.value.toUpperCase() })} />
          </div>
        </div>
        <div className="mt-3 p-3 rounded-xl bg-brand-50 text-xs font-bold text-brand-700">
          <Gem size={12} className="inline" /> Example: a 100 {eco.base_currency} purchase at Tier 3 (1.25×)
          gives <b>{Math.floor(100 * eco.points_per_unit * 1.25)} points</b>,
          the inviter gets <b>{Math.floor(100 * eco.points_per_unit * 1.25 * eco.referral_referrer_pct / 100)}</b>.
        </div>
      </div>

      {/* LOYALTY TIERS */}
      <div className="card p-4">
        <SectionHead title="Loyalty tiers" sub="Granular multiplier per shop relationship" right={
          <button onClick={() => save.mutate({ key: 'loyalty_tiers', value: tiers })} className="btn-primary btn-sm">
            <Save size={13} /> Save</button>} />
        <div className="space-y-2">
          {tiers.map((t, i) => (
            <div key={t.tier} className="grid grid-cols-[40px_1fr_1fr_1fr_1fr] gap-2 items-center">
              <span className="font-black text-sm text-center">T{t.tier}</span>
              <div><label className="label !text-[9px]">Label</label>
                <input className="input !py-1.5 text-xs" value={t.label}
                  onChange={e => { const n = [...tiers]; n[i] = { ...t, label: e.target.value }; setTiers(n); }} /></div>
              <div><label className="label !text-[9px]">Min spent</label>
                <input className="input !py-1.5 text-xs" type="number" value={t.min_spent}
                  onChange={e => { const n = [...tiers]; n[i] = { ...t, min_spent: Number(e.target.value) }; setTiers(n); }} /></div>
              <div><label className="label !text-[9px]">Min orders</label>
                <input className="input !py-1.5 text-xs" type="number" value={t.min_purchases}
                  onChange={e => { const n = [...tiers]; n[i] = { ...t, min_purchases: Number(e.target.value) }; setTiers(n); }} /></div>
              <div><label className="label !text-[9px]">Multiplier</label>
                <input className="input !py-1.5 text-xs font-black" type="number" step="0.05" value={t.multiplier}
                  onChange={e => { const n = [...tiers]; n[i] = { ...t, multiplier: Number(e.target.value) }; setTiers(n); }} /></div>
            </div>))}
        </div>
        <p className="text-[11px] text-ink-400 font-semibold mt-3">
          On top of the tier, users get +0.02× per consecutive weekly purchase (max +0.30×).
        </p>
      </div>

      {/* CURRENCIES */}
      <div className="card p-4">
        <SectionHead title="Currencies" sub={`Converted to ${eco.base_currency}`} />
        <div className="space-y-2">
          {data.currencies.map(c => (
            <div key={c.code} className="flex items-center gap-2">
              <span className="w-14 font-black text-sm">{c.code}</span>
              <input className="input !py-2" type="number" step="0.0001" defaultValue={c.rate_to_base}
                onBlur={e => Number(e.target.value) !== Number(c.rate_to_base) && saveCur.mutate({ code: c.code, rate: Number(e.target.value) })} />
              <span className="text-xs text-ink-400 font-bold whitespace-nowrap">= 1 {c.code}</span>
              {c.code !== eco.base_currency && (
                <button onClick={() => delCur.mutate(c.code)} className="p-2 text-red-500 hover:bg-red-50 rounded-lg"><Trash2 size={14} /></button>)}
            </div>))}
        </div>
        <div className="flex gap-2 mt-3">
          <input className="input !py-2 w-24 uppercase" placeholder="CHF" maxLength={3}
            value={cur.code} onChange={e => setCur({ ...cur, code: e.target.value.toUpperCase() })} />
          <input className="input !py-2" type="number" step="0.0001" placeholder="Rate to base"
            value={cur.rate} onChange={e => setCur({ ...cur, rate: e.target.value })} />
          <button onClick={() => saveCur.mutate({ code: cur.code, rate: Number(cur.rate) })}
            disabled={cur.code.length !== 3 || !cur.rate} className="btn-primary btn-sm"><Plus size={13} /></button>
        </div>
      </div>

      {/* COMBAT */}
      <div className="card p-4">
        <SectionHead title="Arena settings" right={
          <button onClick={() => save.mutate({ key: 'combat', value: combat })} className="btn-primary btn-sm">
            <Save size={13} /> Save</button>} />
        <div className="grid grid-cols-2 gap-3">
          {[['turn_seconds', 'Turn timer (sec)'], ['max_rounds', 'Max rounds'],
            ['energy_max', 'Max energy'], ['energy_regen_minutes', 'Energy regen (min)']].map(([k, l]) => (
            <div key={k}><label className="label">{l}</label>
              <input className="input !py-2" type="number" value={combat[k] ?? ''}
                onChange={e => setCombat({ ...combat, [k]: Number(e.target.value) })} /></div>))}
        </div>
      </div>

      {/* STYLE ANCHOR */}
      {data.style_anchor && (
        <div className="card p-4">
          <SectionHead title="Hero art style anchor" sub="All heroes are generated in this style (gpt-image-2)" />
          <div className="flex gap-3">
            <img src={data.style_anchor.path} alt="" className="w-24 h-36 object-cover rounded-xl bg-ink-100" />
            <div className="text-xs text-ink-500 font-semibold space-y-1">
              <p><b>Model:</b> {data.style_anchor.model}</p>
              <p><b>Created:</b> {new Date(data.style_anchor.created_at).toLocaleString()}</p>
              <p className="text-[11px]">Every new hero is generated with this image as a style reference,
                so the whole roster looks like one coherent art collection — but each character is unique.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}